/**
 * Which customers are waiting on us (ADR 0227, issue #476).
 *
 * One row per customer who spoke last and has not had an answer, beside the
 * conversation itself: the job's own Texts or Emails card, reply box and
 * all, so answering here is answering there. Who is waiting, in what order,
 * and which job each conversation opens on, is core's `buildInbox`, never
 * worked out here. Customers whose messages need nothing are listed below,
 * not hidden (ADR 0222).
 */

import {
  buildInbox,
  messageAttachments,
  jobAddress,
  waitingIsLate,
  waitingLabel,
  type InboxCustomer,
  type Job,
} from "@roof-hawk/core";
import { useMemo, useState } from "react";

import { ErrorNote } from "../../components/ui";
import { t, tn } from "../../i18n";
import { fmtDateTime } from "../../lib/format";
import { navigate, useAsync } from "../../lib/hooks";
import { fetchInbox, markMessagesHandled } from "../../lib/queries";
import { EmailPanel } from "../job/EmailPanel";
import { jobLink } from "../../lib/jobLink";
import { TextsPanel } from "../job/TextsPanel";
import type { AppData } from "../shell/Shell";

/**
 * The customers still waiting on a reply (ADR 0227). Since ADR 0259 it is
 * Iris's inbox on the AI Team page, which draws its own heading: `embedded`
 * leaves out this one's and keeps the counts.
 */
export function MessagesScreen({
  data,
  embedded = false,
}: {
  data: AppData;
  embedded?: boolean;
}) {
  const inbox = useAsync(() => fetchInbox(), []);
  const [picked, setPicked] = useState<string | null>(null);

  const names = useMemo(
    () => new Map(data.contacts.map((c) => [c.id, c.full_name])),
    [data.contacts],
  );
  const contactOfJob = useMemo(
    () => new Map(data.jobs.map((job) => [job.id, job.contact_id])),
    [data.jobs],
  );
  const addressesOfContact = useMemo(
    () =>
      new Map(
        data.contacts.flatMap((c) =>
          c.email ? [[c.id, [c.email]] as const] : [],
        ),
      ),
    [data.contacts],
  );
  const built = useMemo(
    () =>
      inbox.data
        ? buildInbox(
            inbox.data.messages,
            inbox.data.handled,
            contactOfJob,
            addressesOfContact,
            data.jobs,
          )
        : null,
    [inbox.data, contactOfJob, addressesOfContact, data.jobs],
  );
  const jobById = useMemo(
    () => new Map(data.jobs.map((job) => [job.id, job])),
    [data.jobs],
  );

  const everyone = built ? [...built.waiting, ...built.no_reply_needed] : [];
  const current = everyone.find((c) => c.key === picked) ?? everyone[0] ?? null;
  const now = new Date();
  const nameOf = (customer: InboxCustomer) =>
    (customer.contact_id && names.get(customer.contact_id)) ||
    customer.from ||
    t("Unknown sender");

  return (
    <div className="msgs">
      <div className="listhead">
        {embedded ? null : <h2>{t("Messages")}</h2>}
        <span className="count">
          {!built
            ? inbox.loading
              ? t("Loading…")
              : ""
            : t(
                "{waiting} waiting on us · {replied} replied · {noReply} need no reply",
                {
                  waiting: built.waiting.length,
                  replied: built.replied,
                  noReply: built.no_reply_needed.length,
                },
              )}
        </span>
      </div>
      <ErrorNote error={inbox.error} />
      {built && !everyone.length ? (
        <div className="list">
          <div className="msg-empty">
            {t("Nobody is waiting on a reply from the last 14 days.")}
          </div>
        </div>
      ) : null}
      {built && everyone.length ? (
        <div className="inbox">
          <div>
            <CustomerList
              label={t("Waiting on us")}
              customers={built.waiting}
              current={current}
              now={now}
              nameOf={nameOf}
              onPick={setPicked}
            />
            <CustomerList
              label={t("Probably no reply needed")}
              customers={built.no_reply_needed}
              current={current}
              now={now}
              nameOf={nameOf}
              onPick={setPicked}
            />
          </div>
          {current ? (
            <Conversation
              key={current.key}
              customer={current}
              name={nameOf(current)}
              job={(current.job_id && jobById.get(current.job_id)) || null}
              onHandled={inbox.reload}
            />
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/**
 * `waitingLabel` in the app's language. Core says "Waiting 2 hrs"; the
 * number is kept and the unit is looked up, so the Spanish can put the words
 * where Spanish puts them.
 */
function waitingText(since: string, now: Date): string {
  const label = waitingLabel(since, now);
  const match = /^Waiting (\d+) (min|hr|hrs|day|days)$/.exec(label);
  if (!match) return label;
  const n = Number(match[1]);
  const unit = match[2];
  if (unit === "min") return t("Waiting {n} min", { n });
  if (unit === "hr") return t("Waiting {n} hr", { n });
  if (unit === "hrs") return t("Waiting {n} hrs", { n });
  if (unit === "day") return t("Waiting {n} day", { n });
  return t("Waiting {n} days", { n });
}

function CustomerList({
  label,
  customers,
  current,
  now,
  nameOf,
  onPick,
}: {
  label: string;
  customers: readonly InboxCustomer[];
  current: InboxCustomer | null;
  now: Date;
  nameOf: (customer: InboxCustomer) => string;
  onPick: (key: string) => void;
}) {
  if (!customers.length) return null;
  return (
    <section className="msg-group" aria-label={label}>
      <h3>
        {label} <span className="na-hint">({customers.length})</span>
      </h3>
      <div className="list">
        {customers.map((customer) => (
          <button
            key={customer.key}
            type="button"
            className={`msg-row${customer === current ? " on" : ""}`}
            aria-pressed={customer === current}
            onClick={() => onPick(customer.key)}
          >
            <span className="msg-top">
              <span className="msg-name">{nameOf(customer)}</span>
              {customer.same_day ? (
                <span className="msg-tag msg-tag-today">{t("Same day")}</span>
              ) : null}
              <span
                className={`msg-wait${waitingIsLate(customer.waiting_since, now) ? " late" : ""}`}
              >
                {waitingText(customer.waiting_since, now)}
              </span>
            </span>
            {customer.reason ? (
              <span className="msg-reason">{customer.reason}</span>
            ) : null}
            <span className="msg-text">
              {customer.latest.attachments ? (
                <span className="msg-att">
                  {tn(
                    customer.latest.attachments,
                    "1 attachment",
                    "{n} attachments",
                  )}
                </span>
              ) : null}
              {customer.latest.text ||
                (customer.latest.attachments ? "" : t("(no text)"))}
            </span>
          </button>
        ))}
      </div>
    </section>
  );
}

function Conversation({
  customer,
  name,
  job,
  onHandled,
}: {
  customer: InboxCustomer;
  name: string;
  job: Job | null;
  onHandled: () => Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const card = customer.channel === "email" ? "Emails" : "Texts";
  const full = job
    ? jobLink(job.id, card)
    : customer.contact_id
      ? `contact=${customer.contact_id}`
      : null;

  async function markHandled() {
    setBusy(true);
    setError(null);
    try {
      await markMessagesHandled(customer.unanswered.map((m) => m.id));
      await onHandled();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section
      className="inbox-convo"
      aria-label={t("Conversation with {name}", { name })}
    >
      <div className="inbox-head">
        <div>
          <div className="msg-name">{name}</div>
          <div className="na-hint">
            {job ? `#${job.job_number} · ${jobAddress(job)} · ` : ""}
            {customer.channel === "email" ? t("Email") : t("Text")}
          </div>
        </div>
        <div className="inbox-acts">
          {full ? (
            <button
              type="button"
              className="btn-primary"
              onClick={() => navigate(full)}
            >
              {job ? t("Full conversation →") : t("Open contact →")}
            </button>
          ) : null}
          <button
            type="button"
            className="btn-ghost"
            disabled={busy}
            onClick={() => void markHandled()}
          >
            {busy ? t("Marking…") : t("Mark handled")}
          </button>
        </div>
      </div>
      <ErrorNote error={error} />
      {job && customer.channel === "email" ? <EmailPanel job={job} /> : null}
      {job && customer.channel === "text" ? <TextsPanel job={job} /> : null}
      {!job ? (
        <div className="inbox-unmatched">
          <p className="na-hint">
            {t("Not on a job yet, so there is nowhere to reply from here.")}
          </p>
          {customer.unanswered.map((message) => (
            <div key={message.id} className="msg-text">
              {message.subject ? <b>{message.subject}: </b> : null}
              {messageAttachments(message.text).text}{" "}
              <span className="na-hint">· {fmtDateTime(message.sent_at)}</span>
            </div>
          ))}
        </div>
      ) : null}
    </section>
  );
}
